// Typed, host-owned execution boundary for assistant action requests.
//
// The model may emit an id, intent and JSON arguments. It cannot register an
// action, choose an executor, relax policy, or pass arbitrary request options:
// this module rebuilds every API payload from a closed allow-list of fields.

import * as botsApi from "@/api/bots";
import * as dispatcherApi from "@/api/dispatcher";
import * as nativeApi from "@/api/native";
import * as authoringApi from "@/api/assistantAuthoring";
import * as dependenciesApi from "@/api/assistantDependencies";
import * as pipelineApi from "@/api/pipelineBoards";
import * as pluginsApi from "@/api/plugins";
import * as runsApi from "@/api/runs";
import * as workspaceHandoffApi from "@/api/workspaceHandoff";
import { catalogStaleSuffix } from "@/lib/catalogStale";
import { isPathWithinWorkDir } from "@/lib/launchSource";
import {
  ASSISTANT_ACTIONS,
  type AssistantActionDefinition,
  type AssistantActionId,
  type AssistantActionRequest,
} from "./assistantActions";

export interface ValidatedAssistantActionRequest
  extends AssistantActionRequest {
  definition: AssistantActionDefinition;
  title: string;
  detail: string;
  args: Record<string, unknown>;
}

export interface AssistantActionResult {
  message: string;
  href?: string;
  hrefLabel?: string;
  // A closed, host-derived receipt that can safely cross into Copi's
  // host-event transcript. Do not make this a generic result payload.
  receipt?:
    | { git_commit: string }
    | {
        created_bot: {
          name: string;
          // A local Studio editor path, generated by the host from the
          // bot-create response. It is deliberately not model-provided.
          editor_path: string;
        };
      };
}

type UnknownRecord = Record<string, unknown>;

const FULL_GIT_COMMIT_SHA = /^[0-9a-f]{40}$/;
const FULL_GIT_OBJECT_ID = /^[0-9a-f]{40}(?:[0-9a-f]{24})?$/;
const GIT_BRANCH = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,239}$/;

function gitCommitReceipt(commit: unknown): AssistantActionResult["receipt"] {
  if (typeof commit !== "string" || !FULL_GIT_COMMIT_SHA.test(commit)) {
    return undefined;
  }
  return { git_commit: commit };
}

function record(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function text(
  source: UnknownRecord,
  key: string,
  opts: { required?: boolean; max?: number } = {},
): string | undefined {
  const value = source[key];
  if (value === undefined || value === null || value === "") {
    if (opts.required) throw new Error(`Missing ${key}`);
    return undefined;
  }
  if (typeof value !== "string") throw new Error(`${key} must be text`);
  const trimmed = value.trim();
  if (!trimmed && opts.required) throw new Error(`Missing ${key}`);
  const max = opts.max ?? 20_000;
  if (trimmed.length > max) throw new Error(`${key} is too long`);
  return trimmed || undefined;
}

function bool(source: UnknownRecord, key: string): boolean | undefined {
  const value = source[key];
  if (value === undefined) return undefined;
  if (typeof value !== "boolean") throw new Error(`${key} must be true or false`);
  return value;
}

function numberValue(source: UnknownRecord, key: string): number | undefined {
  const value = source[key];
  if (value === undefined) return undefined;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${key} must be a finite number`);
  }
  return value;
}

function displayValue(value: unknown): string {
  if (typeof value === "number" && !Number.isFinite(value)) {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function integerValue(source: UnknownRecord, key: string): number | undefined {
  const value = source[key];
  if (value === undefined) return undefined;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    !Number.isInteger(value)
  ) {
    throw new Error(
      `${key} must be an integer, for example 10 or 20; received ${displayValue(value)}`,
    );
  }
  return value;
}

function strings(
  source: UnknownRecord,
  key: string,
  maxItems = 50,
): string[] | undefined {
  const value = source[key];
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > maxItems) {
    throw new Error(`${key} must be a bounded text list`);
  }
  if (value.some((entry) => typeof entry !== "string")) {
    throw new Error(`${key} must contain only text`);
  }
  return value.map((entry) => entry.trim()).filter(Boolean);
}

function handoffChangedFiles(source: UnknownRecord): string[] | undefined {
  const files = strings(source, "changed_files", 50);
  if (!files) return undefined;
  if (
    files.length !== new Set(files).size ||
    files.some(
      (path) =>
        path.length > 240 ||
        path === "." ||
        path.startsWith("/") ||
        path.includes("\\") ||
        path.split("/").includes(".."),
    )
  ) {
    throw new Error("changed_files must contain unique safe relative paths");
  }
  return files;
}

function handoffPullRequestURL(source: UnknownRecord): string | undefined {
  const value = text(source, "pr_url", { max: 2_048 });
  if (!value) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error("pr_url must be a recognized HTTPS pull-request URL");
  }
  const host = parsed.hostname.toLowerCase();
  const route = parsed.pathname.toLowerCase();
  if (
    parsed.protocol !== "https:" ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    parsed.search !== "" ||
    parsed.hash !== "" ||
    !["github.com", "gitlab.com", "codeberg.org"].includes(host) ||
    (!route.includes("/pull/") &&
      !route.includes("/pulls/") &&
      !route.includes("/merge_requests/"))
  ) {
    throw new Error("pr_url must be a recognized HTTPS pull-request URL");
  }
  return value;
}

function stringMap(
  source: UnknownRecord,
  key: string,
  maxItems = 64,
): Record<string, string> | undefined {
  const value = source[key];
  if (value === undefined) return undefined;
  const valueRecord = record(value);
  if (!valueRecord || Object.keys(valueRecord).length > maxItems) {
    throw new Error(`${key} must be a bounded text map`);
  }
  const out: Record<string, string> = {};
  for (const [name, entry] of Object.entries(valueRecord)) {
    if (!name.trim() || name.length > 128 || typeof entry !== "string") {
      throw new Error(`${key} must contain only bounded text values`);
    }
    if (entry.length > 20_000) throw new Error(`${key}.${name} is too long`);
    out[name] = entry;
  }
  return out;
}

function optional<V>(
  target: UnknownRecord,
  key: string,
  value: V | undefined,
): void {
  if (value !== undefined) target[key] = value;
}

function issueFields(source: UnknownRecord): UnknownRecord {
  const out: UnknownRecord = {};
  optional(out, "title", text(source, "title", { max: 500 }));
  optional(out, "body", text(source, "body"));
  optional(out, "labels", strings(source, "labels"));
  optional(out, "priority", integerValue(source, "priority"));
  optional(out, "assignee", text(source, "assignee", { max: 256 }));
  optional(out, "blockers", strings(source, "blockers"));
  optional(out, "bot", text(source, "bot", { max: 256 }));
  optional(out, "bot_args", stringMap(source, "bot_args"));
  return out;
}

function pipelineFields(source: UnknownRecord): UnknownRecord {
  const out = issueFields(source);
  delete out.assignee;
  return out;
}

function actionDefinition(id: AssistantActionId): AssistantActionDefinition {
  const definition = ASSISTANT_ACTIONS.find((entry) => entry.id === id);
  if (!definition) throw new Error(`Unknown action ${id}`);
  return definition;
}

export function validateAssistantActionRequest(
  request: AssistantActionRequest,
): ValidatedAssistantActionRequest {
  const source = request.args;
  let args: UnknownRecord = {};
  const title = actionDefinition(request.id).label;
  let detail = "";
  const requiredId = (key: string) =>
    text(source, key, { required: true, max: 512 }) as string;

  switch (request.id) {
    case "workspace.handoff": {
      const destinationProject = text(source, "destination_project", {
        required: true,
        max: 512,
      }) as string;
      const summary = text(source, "summary", {
        required: true,
        max: 20_000,
      }) as string;
      args = { destination_project: destinationProject, summary };
      detail = `Continue this Copi conversation in ${destinationProject}`;
      break;
    }
    case "workspace.handoff.complete": {
      const status = text(source, "status", {
        required: true,
        max: 16,
      });
      if (status !== "completed" && status !== "failed") {
        throw new Error("status must be completed or failed");
      }
      const summary = text(source, "summary", {
        required: true,
        max: 4_000,
      }) as string;
      const changedFiles = handoffChangedFiles(source);
      const commitSHA = text(source, "commit_sha", { max: 64 });
      if (commitSHA && !FULL_GIT_OBJECT_ID.test(commitSHA)) {
        throw new Error("commit_sha must be a full lowercase Git object id");
      }
      const branch = text(source, "branch", { max: 240 });
      if (
        branch &&
        (!GIT_BRANCH.test(branch) || branch.includes("..") || branch.includes("//"))
      ) {
        throw new Error("branch is invalid");
      }
      const prURL = handoffPullRequestURL(source);
      const nextStep = text(source, "next_step", { max: 2_000 });
      args = { status, summary };
      optional(args, "changed_files", changedFiles);
      optional(args, "commit_sha", commitSHA);
      optional(args, "branch", branch);
      optional(args, "pr_url", prURL);
      optional(args, "next_step", nextStep);
      detail = `${status === "completed" ? "Report completion" : "Report failure"} to the originating Copi conversation`;
      break;
    }
    case "editor.apply":
    case "editor.save":
    case "editor.files.save":
      throw new Error("Editor actions use the live editor-session protocol");

    case "authoring.git.commit": {
      const message = text(source, "message", { required: true, max: 240 }) as string;
      if (/\r|\n|\0/.test(message)) {
        throw new Error("message must be one line");
      }
      const files = strings(source, "files", 8);
      if (!files || files.length === 0 || new Set(files).size !== files.length) {
        throw new Error("files must contain unique declared authoring paths");
      }
      if (files.some((path) => path === "." || path.startsWith("/") || path.includes("\\") || path.split("/").includes(".."))) {
        throw new Error("files must contain relative authoring paths");
      }
      const sessionId = text(source, "editor_session_id", { required: true, max: 160 }) as string;
      const revision = integerValue(source, "editor_revision");
      if (revision === undefined || revision < 0) {
        throw new Error("editor_revision must be a non-negative integer");
      }
      args = {
        message,
        files,
        editor_session_id: sessionId,
        editor_revision: revision,
      };
      detail = `Commit ${files.length} declared authoring file${files.length === 1 ? "" : "s"}`;
      break;
    }
    case "authoring.git.publish": {
      const commit = text(source, "commit", { required: true, max: 40 }) as string;
      const branch = text(source, "branch", { required: true, max: 128 }) as string;
      if (!/^[0-9a-f]{12,40}$/.test(commit)) throw new Error("commit must be a hexadecimal prefix of at least 12 characters");
      if (!/^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/.test(branch) || branch.includes("..")) throw new Error("branch is invalid");
      const sessionId = text(source, "editor_session_id", { required: true, max: 160 }) as string;
      const revision = integerValue(source, "editor_revision");
      if (revision === undefined || revision < 0) throw new Error("editor_revision must be a non-negative integer");
      args = { commit, branch, editor_session_id: sessionId, editor_revision: revision };
      detail = `Publish ${commit.slice(0, 12)} to fresh branch ${branch}`;
      break;
    }
    case "dependency.bots.update": {
      const name = text(source, "name", { required: true, max: 128 }) as string;
      const ref = text(source, "ref", { required: true, max: 40 }) as string;
      const message = text(source, "message", { required: true, max: 240 }) as string;
      // `text` deliberately trims most human-facing fields. This immutable
      // identifier must instead be copied verbatim, so reject any spelling
      // the host would refuse rather than silently normalizing it.
      if (source.name !== name || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(name)) {
        throw new Error("name is invalid");
      }
      if (source.ref !== ref || !/^[0-9a-f]{40}$/.test(ref)) {
        throw new Error("ref must be a lowercase 40-character Git commit SHA");
      }
      if (source.message !== message || /\r|\n|\0/.test(message)) {
        throw new Error("message must be one line");
      }
      args = { name, ref, message };
      detail = `Update ${name} to ${ref.slice(0, 12)} and commit only bots.lock`;
      break;
    }
    case "dependency.bots.localize": {
      const name = text(source, "name", { required: true, max: 128 }) as string;
      const message = text(source, "message", { required: true, max: 240 }) as string;
      const sessionId = text(source, "editor_session_id", { required: true, max: 160 }) as string;
      const revision = integerValue(source, "editor_revision");
      if (source.name !== name || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(name)) {
        throw new Error("name is invalid");
      }
      if (source.message !== message || /\r|\n|\0/.test(message)) {
        throw new Error("message must be one line");
      }
      if (revision === undefined || revision < 0) {
        throw new Error("editor_revision must be a non-negative integer");
      }
      args = { name, message, editor_session_id: sessionId, editor_revision: revision };
      detail = `Localize ${name} at the open consumer bundle's plugins/${name}; commit that source, then only bots.lock`;
      break;
    }

    case "board.issue.create": {
      const issue = issueFields(source);
      issue.title = text(source, "title", { required: true, max: 500 });
      optional(issue, "state", text(source, "state", { max: 128 }));
      args = issue;
      detail = `Create “${issue.title}” on the board`;
      break;
    }
    case "board.issue.update": {
      const issueId = requiredId("issue_id");
      const patch = issueFields(source);
      if (Object.keys(patch).length === 0) throw new Error("No issue fields to update");
      args = { issue_id: issueId, patch };
      detail = `Update board issue ${issueId}`;
      break;
    }
    case "board.issue.transition": {
      const issueId = requiredId("issue_id");
      const to = requiredId("to");
      args = { issue_id: issueId, to };
      detail = `Move board issue ${issueId} to ${to}`;
      break;
    }
    case "board.issue.comment": {
      const issueId = requiredId("issue_id");
      const body = text(source, "body", { required: true }) as string;
      args = { issue_id: issueId, body };
      detail = `Comment on board issue ${issueId}: “${body.slice(0, 160)}${body.length > 160 ? "…" : ""}”`;
      break;
    }
    case "board.issue.delete": {
      const issueId = requiredId("issue_id");
      args = { issue_id: issueId };
      detail = `Permanently delete board issue ${issueId}`;
      break;
    }

    case "pipeline.task.create": {
      const task = pipelineFields(source);
      task.title = text(source, "title", { required: true, max: 500 });
      task.bot = requiredId("bot");
      optional(task, "start", bool(source, "start"));
      optional(task, "upsert", bool(source, "upsert"));
      args = task;
      detail = `Create pipeline “${task.title}” with bot ${task.bot}`;
      break;
    }
    case "pipeline.task.update": {
      const taskId = requiredId("task_id");
      const patch = pipelineFields(source);
      if (Object.keys(patch).length === 0) throw new Error("No pipeline fields to update");
      args = { task_id: taskId, patch };
      detail = `Update pipeline task ${taskId}`;
      break;
    }
    case "pipeline.task.ready": {
      const taskId = requiredId("task_id");
      const ready = bool(source, "ready");
      if (ready === undefined) throw new Error("Missing ready");
      args = { task_id: taskId, ready };
      detail = `${ready ? "Mark" : "Unmark"} pipeline task ${taskId} ${ready ? "ready" : "as ready"}`;
      break;
    }
    case "pipeline.task.reset": {
      const taskId = requiredId("task_id");
      args = { task_id: taskId };
      optional(args, "fresh", bool(source, "fresh"));
      detail = `Reset pipeline task ${taskId}`;
      break;
    }
    case "pipeline.task.launch":
    case "pipeline.task.close":
    case "pipeline.task.delete": {
      const taskId = requiredId("task_id");
      args = { task_id: taskId };
      const parts = request.id.split(".");
      const verb = parts[parts.length - 1] ?? "execute";
      detail = `${verb.charAt(0).toUpperCase()}${verb.slice(1)} pipeline task ${taskId}`;
      break;
    }

    case "run.launch": {
      const bot = text(source, "bot", { max: 512 });
      const filePath = text(source, "file_path", { max: 4_096 });
      if (!bot && !filePath) {
        throw new Error("run.launch requires bot or file_path");
      }
      if (bot && filePath) {
        throw new Error("run.launch accepts either bot or file_path, not both");
      }
      optional(args, "vars", stringMap(source, "vars"));
      optional(args, "preset", text(source, "preset", { max: 256 }));
      optional(args, "source_run_id", text(source, "source_run_id", { max: 512 }));
      optional(args, "instructions", text(source, "instructions", { max: 20_000 }));
      optional(args, "bot", bot);
      optional(args, "file_path", filePath);
      if (filePath && args.source_run_id) {
        throw new Error("run.launch file_path cannot be combined with source_run_id");
      }
      detail = filePath
        ? `Launch local workflow ${filePath}`
        : args.source_run_id
          ? `Launch catalog bot ${bot} to address failed run ${args.source_run_id}`
          : `Launch catalog bot ${bot}`;
      break;
    }
    case "run.resume": {
      const runId = requiredId("run_id");
      args = { run_id: runId };
      const filePath = text(source, "file_path", { max: 4_096 });
      optional(args, "file_path", filePath);
      detail = filePath
        ? `Resume run ${runId} from ${filePath}`
        : `Resume run ${runId}`;
      break;
    }
    case "run.pause":
    case "run.cancel":
    case "run.delete": {
      const runId = requiredId("run_id");
      args = { run_id: runId };
      const parts = request.id.split(".");
      const verb = parts[parts.length - 1] ?? "execute";
      detail = `${verb.charAt(0).toUpperCase()}${verb.slice(1)} run ${runId}`;
      break;
    }
    case "run.rewind": {
      const runId = requiredId("run_id");
      const nodeId = text(source, "node_id", { max: 512 });
      const auto = bool(source, "auto") ?? false;
      if (auto && nodeId) {
        throw new Error("run.rewind accepts either auto:true or node_id, not both");
      }
      if (!auto && !nodeId) {
        throw new Error("run.rewind requires auto:true or node_id");
      }
      // Deliberately rebuilt rather than forwarded: source_path, keep_files
      // and every file-restoration spelling are outside model authority.
      args = {
        run_id: runId,
        ...(auto ? { auto: true } : { node_id: nodeId }),
        restore_scope: "none",
      };
      detail = auto
        ? `Automatically rewind run ${runId} to the earliest node affected by the current workflow source; leave workspace files untouched`
        : `Rewind run ${runId} to node ${nodeId}; leave workspace files untouched`;
      break;
    }
    case "run.rename": {
      const runId = requiredId("run_id");
      const name = text(source, "name", { required: true, max: 160 }) as string;
      args = { run_id: runId, name };
      detail = `Rename run ${runId} to “${name}”`;
      break;
    }
    case "run.watch": {
      const targetRunId = requiredId("target_run_id");
      const rawMode = text(source, "mode", { max: 16 }) ?? "diagnose";
      if (rawMode !== "diagnose" && rawMode !== "propose") {
        throw new Error("mode must be diagnose or propose");
      }
      const cooldownSeconds = numberValue(source, "cooldown_seconds");
      const rawKinds = source.kinds;
      const allowedKinds = new Set(["run.failed", "run.finished", "run.cancelled", "run.stalled", "run.paused"]);
      let kinds: string[] | undefined;
      if (rawKinds !== undefined) {
        if (!Array.isArray(rawKinds) || rawKinds.length < 1 || rawKinds.length > 4 || rawKinds.some((kind) => typeof kind !== "string" || !allowedKinds.has(kind))) {
          throw new Error("kinds must contain one to four distinct supported run outcomes");
        }
        kinds = [...new Set(rawKinds)];
        if (kinds.length !== rawKinds.length) throw new Error("kinds must not contain duplicates");
      }
      if (cooldownSeconds !== undefined && (!Number.isInteger(cooldownSeconds) || cooldownSeconds < 0 || cooldownSeconds > 86_400)) {
        throw new Error("cooldown_seconds must be an integer between 0 and 86400");
      }
      args = { target_run_id: targetRunId, mode: rawMode };
      optional(args, "cooldown_seconds", cooldownSeconds);
      optional(args, "kinds", kinds);
      detail = `Watch run ${targetRunId} in ${rawMode} mode`;
      break;
    }
    case "run.unwatch": {
      const watchId = requiredId("watch_id");
      args = { watch_id: watchId };
      // The run-addressed stop (ADR-103) when the assistant names the
      // run it is watching; without it the fixed route still serves.
      optional(args, "target_run_id", text(source, "target_run_id", { max: 200 }));
      detail = `Stop run watch ${watchId}`;
      break;
    }

    case "dispatcher.start":
    case "dispatcher.pause":
    case "dispatcher.resume":
    case "dispatcher.stop":
      detail = actionDefinition(request.id).description;
      break;

    case "bot.create": {
      const slug = text(source, "slug", { required: true, max: 128 }) as string;
      if (!/^[a-z0-9][a-z0-9_-]*$/.test(slug)) throw new Error("Invalid bot slug");
      const instructions = text(source, "instructions", { required: true }) as string;
      args = { slug, instructions };
      for (const key of ["display_name", "icon", "description", "when_to_use", "model", "backend", "permission", "max_duration"] as const) {
        optional(args, key, text(source, key, { max: key === "description" || key === "when_to_use" ? 20_000 : 512 }));
      }
      optional(args, "skills", strings(source, "skills"));
      optional(args, "capabilities", strings(source, "capabilities"));
      optional(args, "worktree", bool(source, "worktree"));
      optional(args, "sandbox", bool(source, "sandbox"));
      optional(args, "max_cost_usd", numberValue(source, "max_cost_usd"));
      detail = `Create bot bundle ${slug}`;
      break;
    }
    case "bot.install": {
      const url = text(source, "url", { required: true, max: 4_096 }) as string;
      args = { url };
      optional(args, "ref", text(source, "ref", { max: 512 }));
      optional(args, "path", text(source, "path", { max: 2_048 }));
      optional(args, "name", text(source, "name", { max: 128 }));
      optional(args, "force", bool(source, "force"));
      detail = `Install bot from ${url}`;
      break;
    }
    case "plugin.enable":
    case "plugin.disable":
    case "plugin.uninstall": {
      const name = requiredId("name");
      args = { name };
      detail = `${request.id.endsWith("enable") ? "Enable" : request.id.endsWith("disable") ? "Disable" : "Uninstall"} plugin ${name}`;
      break;
    }
    case "plugin.install": {
      const sourceValue = text(source, "source", { required: true, max: 4_096 }) as string;
      args = { source: sourceValue };
      detail = `Install plugin from ${sourceValue}`;
      break;
    }
  }

  return {
    ...request,
    args,
    definition: actionDefinition(request.id),
    title,
    detail,
  };
}

export async function executeAssistantAction(
  request: ValidatedAssistantActionRequest,
  context: {
    assistantRunId?: string | null;
    workspaceHandoffId?: string | null;
    // Host-selected only. `validateAssistantActionRequest` strips a model-
    // supplied `force`, so source drift always requires a second operator
    // gesture in AssistantActionOffer.
    forceResume?: boolean;
    authoringSnapshot?: authoringApi.AssistantAuthoringSnapshot;
  } = {},
): Promise<AssistantActionResult> {
  const args = request.args as UnknownRecord;
  const id = (key: string) => args[key] as string;
  switch (request.id) {
    case "workspace.handoff": {
      if (!context.assistantRunId) {
        throw new Error("The source Copi run is unavailable for this handoff");
      }
      const handoff = await workspaceHandoffApi.createWorkspaceHandoff(
        id("destination_project"),
        id("summary"),
        context.assistantRunId,
      );
      window.parent.postMessage(
        {
          source: "iterion-pane",
          type: "workspace-handoff",
          projectId: handoff.destination_project_id,
          ticket: handoff.ticket,
        },
        window.location.origin,
      );
      return {
        message: `Continuing with Copi in ${handoff.destination_project_name}`,
      };
    }
    case "workspace.handoff.complete": {
      if (!context.workspaceHandoffId || !context.assistantRunId) {
        throw new Error("This Copi conversation is not bound to a project handoff");
      }
      await workspaceHandoffApi.completeWorkspaceHandoff(
        context.workspaceHandoffId,
        context.assistantRunId,
        args as unknown as workspaceHandoffApi.WorkspaceHandoffReceipt,
      );
      return { message: "Reported this result to the originating Copi conversation" };
    }
    case "editor.apply":
    case "editor.save":
    case "editor.files.save":
      throw new Error("Editor actions require a live editor session");
    case "authoring.git.commit": {
      if (!context.authoringSnapshot) {
        throw new Error("The original authoring editor session is no longer available. Ask Copi again from the open bot.");
      }
      const result = await authoringApi.commitAssistantAuthoringGit(
        context.authoringSnapshot,
        args.files as string[],
        id("message"),
      );
      const receipt = gitCommitReceipt(result.commit);
      return {
        message: `Created commit ${result.commit.slice(0, 12)}`,
        ...(receipt ? { receipt } : {}),
      };
    }
    case "authoring.git.publish": {
      if (!context.authoringSnapshot) throw new Error("The original authoring editor session is no longer available. Ask Copi again from the open bot.");
      const result = await authoringApi.publishAssistantAuthoringGit(context.authoringSnapshot, id("commit"), id("branch"));
      return { message: `Published ${result.commit.slice(0, 12)} to ${result.branch}` };
    }
    case "dependency.bots.update": {
      const result = await dependenciesApi.updateAssistantBotDependency(
        id("name"), id("ref"), id("message"),
      );
      return {
        message: `Updated ${result.name} from ${result.previous_ref.slice(0, 12)} to ${result.ref.slice(0, 12)}; committed bots.lock as ${result.commit.slice(0, 12)}`,
      };
    }
    case "dependency.bots.localize": {
      if (!context.authoringSnapshot) {
        throw new Error("The original authoring editor session is no longer available. Ask Copi again from the open consumer bot.");
      }
      const result = await dependenciesApi.localizeAssistantBotDependency(
        context.authoringSnapshot,
        id("name"),
        id("message"),
      );
      const resumed = result.resumed_after_import ? " (resumed after its source import)" : "";
      return {
        message: `Localized ${result.name} at ${result.local_source}; source commit ${result.source_commit.slice(0, 12)}, bots.lock commit ${result.lock_commit.slice(0, 12)}${resumed}`,
      };
    }
    case "board.issue.create": {
      const issue = await nativeApi.createIssue(args as unknown as nativeApi.NativeIssueCreate);
      if (context.assistantRunId && issue.state === "ready") {
        await runsApi.addWatch(context.assistantRunId, issue.id).catch(() => undefined);
      }
      return { message: `Created board issue ${issue.id}`, href: "/board", hrefLabel: "Open board" };
    }
    case "board.issue.update": {
      await nativeApi.patchIssue(id("issue_id"), args.patch as nativeApi.NativeIssuePatch);
      return { message: `Updated board issue ${id("issue_id")}`, href: "/board", hrefLabel: "Open board" };
    }
    case "board.issue.transition":
      await nativeApi.transitionIssue(id("issue_id"), id("to"));
      if (context.assistantRunId && id("to") === "ready") {
        await runsApi.addWatch(context.assistantRunId, id("issue_id")).catch(() => undefined);
      }
      return { message: `Moved board issue ${id("issue_id")} to ${id("to")}` };
    case "board.issue.comment":
      await nativeApi.commentIssue(id("issue_id"), id("body"));
      return { message: `Commented on board issue ${id("issue_id")}` };
    case "board.issue.delete":
      await nativeApi.deleteIssue(id("issue_id"));
      return { message: `Deleted board issue ${id("issue_id")}` };
    case "pipeline.task.create": {
      const issue = await pipelineApi.createPipelineTask(args as unknown as pipelineApi.CreatePipelineTaskInput);
      if (context.assistantRunId && args.start === true) {
        await runsApi.addWatch(context.assistantRunId, issue.id).catch(() => undefined);
      }
      return { message: `Created pipeline task ${issue.id}`, href: `/pipelines/cards/issue/${encodeURIComponent(issue.id)}`, hrefLabel: "Open pipeline" };
    }
    case "pipeline.task.update":
      await pipelineApi.updatePipelineTask(id("task_id"), args.patch as pipelineApi.PipelineTaskPatch);
      return { message: `Updated pipeline task ${id("task_id")}` };
    case "pipeline.task.ready":
      await pipelineApi.markPipelineTaskReady(id("task_id"), args.ready as boolean);
      return { message: `${args.ready ? "Marked" : "Unmarked"} pipeline task ${id("task_id")} ${args.ready ? "ready" : "as ready"}` };
    case "pipeline.task.launch":
      await pipelineApi.launchPipelineTask(id("task_id"));
      return { message: `Launched pipeline task ${id("task_id")}` };
    case "pipeline.task.reset":
      await pipelineApi.resetPipelineTask(id("task_id"), { fresh: args.fresh === true });
      return { message: `Reset pipeline task ${id("task_id")}` };
    case "pipeline.task.close":
      await pipelineApi.closePipelineTask(id("task_id"));
      return { message: `Closed pipeline task ${id("task_id")}` };
    case "pipeline.task.delete":
      await pipelineApi.deletePipelineTask(id("task_id"));
      return { message: `Deleted pipeline task ${id("task_id")}` };
    case "run.launch": {
      const botId = typeof args.bot === "string" ? args.bot : undefined;
      const filePath = typeof args.file_path === "string" ? args.file_path : undefined;
      let bot: Awaited<ReturnType<typeof botsApi.getBot>> | undefined;
      if (botId) {
        bot = await botsApi.getBot(botId);
      }
      if (filePath) {
        const serverInfo = await runsApi.getServerInfo();
        if (
          serverInfo.mode !== "local" ||
          !isPathWithinWorkDir(filePath, serverInfo.work_dir) ||
          !filePath.toLowerCase().endsWith(".bot")
        ) {
          throw new Error(
            "run.launch file_path must be a .bot file inside the active local work directory",
          );
        }
      }
      if (args.source_run_id) {
        if (!bot) {
          throw new Error("run.launch source_run_id requires a catalog bot");
        }
        // source_run_id means "repair the source package that produced this
        // failure", not "relaunch the failed workflow". Catch the common
        // category error before spending a POST: delegated workers must opt
        // into the host-owned context/instruction envelope. The server still
        // performs the authoritative worktree:auto + var checks at admission.
        const declared = new Set(
          bot.vars?.fields?.map((field) => field.name).filter(Boolean) ?? [],
        );
        const missing = ["failure_context", "delegation_instructions"].filter(
          (name) => !declared.has(name),
        );
        if (missing.length > 0) {
          throw new Error(
            `Delegated launch unavailable: catalog bot ${bot.name} is not a repair worker (missing ${missing.join(", ")}). Choose a worker that declares the delegation variables and worktree: auto; do not relaunch the failed workflow with source_run_id.`,
          );
        }
      }
      const launched = await runsApi.createRun({
        file_path: filePath ?? bot!.path,
        ...(bot ? { bot_id: bot.name } : {}),
        ...(args.vars ? { vars: args.vars as Record<string, string> } : {}),
        ...(args.preset ? { preset: id("preset") } : {}),
        ...(args.source_run_id ? { source_run_id: id("source_run_id") } : {}),
        ...(args.instructions ? { instructions: id("instructions") } : {}),
      });
      if (context.assistantRunId) {
        await runsApi.createAssistantRunWatch(launched.run_id, {
          assistant_run_id: context.assistantRunId,
          mode: "propose",
          kinds: ["run.paused", "run.failed", "run.stalled", "run.finished"],
          cooldown_seconds: 0,
        });
      }
      const label = bot?.display_name || bot?.name || filePath;
      return { message: `Launched ${label}`, href: `/runs/${encodeURIComponent(launched.run_id)}`, hrefLabel: "Open run" };
    }
    case "run.pause":
      await runsApi.pauseRun(id("run_id"));
      return { message: `Paused run ${id("run_id")}` };
    case "run.resume": {
      const resumeRequest: { file_path?: string; force?: boolean } =
        context.forceResume ? { force: true } : {};
      const filePath = typeof args.file_path === "string" ? args.file_path : undefined;
      if (filePath) {
        const serverInfo = await runsApi.getServerInfo();
        if (
          serverInfo.mode !== "local" ||
          !isPathWithinWorkDir(filePath, serverInfo.work_dir)
        ) {
          throw new Error(
            "run.resume file_path must be inside the active local work directory",
          );
        }
        resumeRequest.file_path = filePath;
      }
      await runsApi.resumeRun(id("run_id"), resumeRequest);
      // Same convention as run.launch above and the board transitions: an
      // action that STARTS work arms the assistant on its outcome. Without
      // it a resume is a one-way instruction — the run fails again minutes
      // later and the assistant learns nothing until the operator comes back
      // to ask. That is the whole difference between "it resumed the run"
      // and "it is seeing this through".
      //
      // A resume asserts the canonical supervised policy, even when that
      // replaces a narrower explicit policy for the same assistant/target.
      // Pauses, failures, stalls and completion all require the assistant to
      // observe the run again. run.cancelled is deliberately omitted: rewind
      // transiently uses that outcome and the server discards those stale
      // episodes; the API also deliberately caps this list at four kinds.
      // Best-effort — losing the watch must not turn a successful resume into
      // a reported failure.
      if (context.assistantRunId) {
        await runsApi
          .createAssistantRunWatch(id("run_id"), {
            assistant_run_id: context.assistantRunId,
            mode: "propose",
            kinds: ["run.paused", "run.failed", "run.stalled", "run.finished"],
            cooldown_seconds: 0,
          })
          .catch(() => undefined);
      }
      return {
        message: context.forceResume
          ? `Resumed run ${id("run_id")} with the current workflow source`
          : `Resumed run ${id("run_id")}`,
        href: `/runs/${encodeURIComponent(id("run_id"))}`,
        hrefLabel: "Open run",
      };
    }
    case "run.rewind": {
      const rewound = await runsApi.rewindRun(id("run_id"), {
        ...(args.auto === true ? { auto: true } : { node_id: id("node_id") }),
        restore_scope: "none",
      });
      return {
        message: `Rewound run ${rewound.run_id} to ${rewound.node_id}; invalidated ${rewound.dropped_nodes.length} node${rewound.dropped_nodes.length === 1 ? "" : "s"}. The run remains parked until it is resumed separately`,
        href: `/runs/${encodeURIComponent(rewound.run_id)}`,
        hrefLabel: "Open run",
      };
    }
    case "run.cancel":
      await runsApi.cancelRun(id("run_id"));
      return { message: `Cancelled run ${id("run_id")}` };
    case "run.rename":
      await runsApi.renameRun(id("run_id"), id("name"));
      return { message: `Renamed run ${id("run_id")}` };
    case "run.watch": {
      if (!context.assistantRunId) throw new Error("The assistant run is not available");
      const watch = await runsApi.createAssistantRunWatch(id("target_run_id"), {
        assistant_run_id: context.assistantRunId,
        mode: id("mode") as "diagnose" | "propose",
        cooldown_seconds: args.cooldown_seconds as number | undefined,
        ...(Array.isArray(args.kinds) ? { kinds: args.kinds as Array<"run.failed" | "run.finished" | "run.cancelled" | "run.stalled" | "run.paused"> } : {}),
      });
      return {
        message: watch.covered_run_id
          ? `Run ${watch.covered_run_id} is already covered by root watch ${watch.id} on ${watch.target_run_id}`
          : `Watching run tree ${watch.target_run_id}`,
      };
    }
    case "run.unwatch":
      await runsApi.stopAssistantRunWatch(id("watch_id"), id("target_run_id"));
      return { message: `Stopped run watch ${id("watch_id")}` };
    case "run.delete":
      await runsApi.deleteRun(id("run_id"));
      return { message: `Deleted run ${id("run_id")}` };
    case "dispatcher.start":
      await dispatcherApi.start();
      return { message: "Started the dispatcher" };
    case "dispatcher.pause":
      await dispatcherApi.pause();
      return { message: "Paused the dispatcher" };
    case "dispatcher.resume":
      await dispatcherApi.resume();
      return { message: "Resumed the dispatcher" };
    case "dispatcher.stop":
      await dispatcherApi.stop();
      return { message: "Stopped the dispatcher" };
    case "bot.create": {
      const bot = await botsApi.createBot(args as unknown as botsApi.BotCreateSpec);
      const editorPath = bot.rel_path ? `${bot.rel_path}/main.bot` : undefined;
      return {
        message: `Created bot ${bot.display_name || bot.name}${catalogStaleSuffix(bot)}`,
        href: `/editor?file=${encodeURIComponent(editorPath ?? bot.path)}`,
        hrefLabel: "Open bot",
        ...(editorPath
          ? { receipt: { created_bot: { name: bot.name, editor_path: editorPath } } }
          : {}),
      };
    }
    case "bot.install": {
      const installed = await botsApi.installBot(args as unknown as botsApi.InstallBotRequest);
      return {
        message: `Installed bot ${installed.name}${catalogStaleSuffix(installed)}`,
        href: `/bots`,
        hrefLabel: "Open bots",
      };
    }
    case "plugin.enable":
      await pluginsApi.setPluginEnabled(id("name"), true);
      return { message: `Enabled plugin ${id("name")}` };
    case "plugin.disable":
      await pluginsApi.setPluginEnabled(id("name"), false);
      return { message: `Disabled plugin ${id("name")}` };
    case "plugin.install": {
      const installed = await pluginsApi.installPlugin(id("source"));
      return { message: `Installed plugin ${installed.name}`, href: "/plugins", hrefLabel: "Open plugins" };
    }
    case "plugin.uninstall":
      await pluginsApi.uninstallPlugin(id("name"));
      return { message: `Uninstalled plugin ${id("name")}` };
  }
}
